import { setGA4Event } from "@hardware/Store/Handlers/Utilities/CustomTracking";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { fetch } from "@insite/client-framework/ServerSideRendering";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface PaperworkStyles {
    titleText?: TypographyProps;
    wrapper?: InjectableCss;
    typographyProps?: InjectableCss;
}

export const totalStyles: PaperworkStyles = {
    wrapper: {
        css: css`
            padding-bottom: 10px;
        `,
    },
    titleText: {
        variant: "h3",
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 5px;
            font-size: 16px;
            padding-top: 10px;
        `,
    },
    typographyProps: {
        css: css`
            text-align: left;
            font-size: 15px;
            font-weight: 700;
            color: #094e66;
            text-decoration: none;
        `,
    },
};

const styles = totalStyles;

const Paperwork: FC = () => {
    const { value: order } = useContext(OrderStateContext);
    const paperworkUrl =
        order && order.status === "Invoiced"
            ? `/api/v1/orderpaperwork/GetPdf?orderNumber=${order.erpOrderNumber}&customerNumber=${order.customerSequence}`
            : null;

    if (!order || order.status !== "Invoiced") {
        return null;
    }

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        try {
            // Fetch the document

            if (paperworkUrl === null) {
                return;
            }

            const response = await fetch(paperworkUrl, {
                credentials: "include",
            });

            if (!response.ok) {
                throw new Error("Network response was not ok.");
            }
            const fileNameHeader = response.headers.get("X-File-Name");
            if (fileNameHeader) {
                const fileName = fileNameHeader.split(".")[0];
                const fileExtension = fileNameHeader.split(".")[1].toUpperCase();
                setGA4Event("download_paperwork", [], {
                    fileExtension,
                    fileName,
                });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to download the document:", error);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="paperwork_OrderDetailInformation">
            <Typography {...styles.titleText}>{translate("Paperwork")}</Typography>
            <Link {...styles.typographyProps} target="_blank" href={paperworkUrl ?? ""} onClick={handleClick}>
                {translate("Click here to download related documents")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: Paperwork,
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
